import {DataTableSortStatus} from 'mantine-datatable';
import {useTableSort} from '@flexinet/ui-components';

import {useApplicationScreenshotsQuery} from './useApplicationScreenshotsQuery';
import {useApplicationScreenshotsSearch} from './useApplicationScreenshotsSearch';
import {useApplicationScreenshotsTableColumns} from './useApplicationScreenshotsTableColumns';
import {ApplicationScreenshot} from '../types';

// NEWEST-CREATED FIRST by default (owner, 2026-09-01).
//
// `created` is NULLABLE on this entity — types.ts makes every field except `id`
// optional, so there is no non-null timestamp to prefer, and the escape hatch the
// library documents (default on a `!` field, as synthetic-users does with `bornAt`)
// is unavailable here. The owner chose the nullable key over changing the schema:
// the API populates `created` on every record it writes, so a missing value is a
// type-level possibility rather than something an operator sees, and the
// alternative would block this module behind an API deploy.
//
// Nulls sort LAST in both directions, which is what makes the nullable key safe
// enough to default on — that rule lives in the shared sortRecords (FNP-912), not
// in a comparator copied into this module (FNP-923). If rows with no `created`
// ever appear in real data, that is an API defect to file against
// flexinet-screenshots-api, not a reason to revisit this default.
const DEFAULT_SORT_STATUS: DataTableSortStatus<ApplicationScreenshot> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useApplicationScreenshots = (filters: Record<string, string> = {}) => {
    const {
        data: items = [],
        isLoading: loading,
        isError,
    } = useApplicationScreenshotsQuery(filters);

    const {keyword} = useApplicationScreenshotsSearch();

    const columns = useApplicationScreenshotsTableColumns();

    // Client-side keyword filtering, over the records LOADED SO FAR. Today the list
    // query is unpaginated so that is every record; once the API can return a cursor
    // (FNP-561) this narrows to the fetched pages, the same scope the sort works on.
    const matched = items.filter(item =>
        !keyword ||
        item.url?.toLowerCase().includes(keyword.toLowerCase()) ||
        item.path?.toLowerCase().includes(keyword.toLowerCase()) ||
        item.status?.toLowerCase().includes(keyword.toLowerCase())
    );

    // Sort state is COMPONENT-level, not the module-scoped store: how one operator
    // is looking at the table right now is not shared with sibling components, unlike
    // the keyword and the filters (FNP-815).
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort(matched, DEFAULT_SORT_STATUS);

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        columns,
    };
};
